import { Link as RouterLink } from 'react-router-dom';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { RightOutlined } from '@ant-design/icons';
import { ArticleMeta } from 'types/blog';
import { articlePath, formatDate } from 'const/blog';

// ==============================|| 記事カード ||============================== //
//
// 一覧ページと、記事末尾の関連記事の両方で使う。
// サムネイル画像は持たせていない。記事ごとに画像を用意する運用は続かず、
// 用意できなかった記事だけ見た目が崩れるため、文字組みで差をつける作りにしている。

const primaryColor = '#2aa1af';

/**
 * headingLevel は置き場所にあわせて変える。
 * 一覧ページは h1 の直下に並ぶので h2、記事末尾の「あわせて読みたい」は
 * その h2 の下なので h3。見出しの段を飛ばすと、文書構造を読む機械から見て
 * 階層が崩れて見える。
 */
const ArticleCard = ({ article, headingLevel = 'h3' }: { article: ArticleMeta; headingLevel?: 'h2' | 'h3' }) => (
    <Box
        component={RouterLink}
        to={articlePath(article.slug)}
        sx={{
            display: 'block',
            height: '100%',
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: `1px solid ${alpha('#111827', 0.1)}`,
            bgcolor: '#fff',
            textDecoration: 'none',
            transition: 'border-color .2s ease, box-shadow .2s ease, transform .2s ease',
            '&:hover': {
                borderColor: alpha(primaryColor, 0.5),
                boxShadow: `0 10px 24px ${alpha(primaryColor, 0.14)}`,
                transform: 'translateY(-2px)'
            }
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.75 }}>
            <Typography
                component="span"
                sx={{ px: 1.25, py: 0.35, borderRadius: 1, bgcolor: alpha(primaryColor, 0.12), color: '#0f3d43', fontSize: '0.7rem', fontWeight: 800 }}
            >
                {article.category}
            </Typography>
            <Typography component="span" sx={{ color: '#9ca3af', fontSize: '0.75rem', fontWeight: 600 }}>
                <time dateTime={article.updatedAt}>{formatDate(article.updatedAt)}</time>
            </Typography>
        </Stack>

        <Typography
            component={headingLevel}
            sx={{ fontSize: { xs: '1.02rem', md: '1.1rem' }, fontWeight: 800, lineHeight: 1.65, color: '#0f3d43', mb: 1.5 }}
        >
            {article.title}
        </Typography>

        <Typography component="p" sx={{ color: '#6b7280', fontSize: '0.88rem', lineHeight: 1.9, mb: 2 }}>
            {article.excerpt}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: primaryColor, fontWeight: 800, fontSize: '0.82rem' }}>
            <Box component="span">続きを読む</Box>
            <Box component="span" sx={{ display: 'flex', fontSize: '0.7rem' }}>
                <RightOutlined />
            </Box>
        </Stack>
    </Box>
);

export default ArticleCard;
